/* Small reusable pieces shared by more than one screen. */

import { h, icon, ICONS, type Child, type IconPath } from '../core/dom.ts';

/**
 * Handlers for a dialog's scrim: dismiss on a click at the scrim, but not on a
 * drag that merely ended there.
 *
 * Testing `e.target === e.currentTarget` on click alone is not enough, and reads
 * as though it were. A click's target is the nearest common ancestor of where the
 * button went down and where it came up — so selecting text in a note field and
 * releasing past the panel's edge produces one click whose target IS the scrim,
 * which closed the dialog mid-selection and threw away what had been typed. The
 * press has to have landed on the scrim too.
 *
 * `blocked` is consulted at click time — a dialog mid-save should not be
 * dismissible.
 */
export function scrim(
  close: () => void,
  blocked?: () => boolean,
): { onmousedown: (e: MouseEvent) => void; onclick: (e: MouseEvent) => void } {
  let pressedScrim = false;
  return {
    onmousedown: (e: MouseEvent) => {
      pressedScrim = e.target === e.currentTarget;
    },
    onclick: (e: MouseEvent) => {
      const fromScrim = pressedScrim;
      pressedScrim = false;
      if (fromScrim && e.target === e.currentTarget && !blocked?.()) close();
    },
  };
}

/** A bordered card. Pass `clip` when the content has its own edge-to-edge rows. */
export function card(
  opts: { clip?: boolean; pad?: boolean; class?: string; style?: string },
  ...children: Child[]
): HTMLDivElement {
  return h(
    'div',
    {
      class: ['card', opts.clip && 'card--clip', opts.pad && 'card--pad', opts.class],
      ...(opts.style ? { style: opts.style } : {}),
    },
    ...children,
  );
}

/** A card header: title, optional hint, optional trailing nodes. */
export function cardHead(title: string, hint?: string, ...trailing: Child[]): HTMLDivElement {
  return h(
    'div',
    { class: 'card__head' },
    h('span', { class: 'card__title', text: title }),
    hint && h('span', { class: 'card__hint', text: hint }),
    trailing.length ? h('span', { class: 'spacer' }) : null,
    ...trailing,
  );
}

/**
 * A card header that folds the block under it.
 *
 * The title and the hint are inside the button, the trailing controls are not:
 * a block's own actions — Add inspection, Log defect, Show history — must stay
 * pressable without opening or closing anything, and a button inside a button is
 * not a thing.
 *
 * Takes the state and the handler rather than reading a store, like every other
 * widget here: this module is drawn by two applications and by the checks, and a
 * widget that reached for state would be a widget that could only be rendered
 * one way.
 */
export function foldHead(
  open: boolean,
  toggle: (() => void) | null,
  title: string,
  hint?: string,
  ...trailing: Child[]
): HTMLDivElement {
  return h(
    'div',
    { class: 'card__head card__head--fold' },
    /* No toggle: the block is held open by whatever is showing it, and the
       heading is a heading — no chevron promising a fold that would do nothing. */
    toggle === null
      ? h(
          'div',
          { class: 'fold__btn fold__btn--fixed' },
          h('span', { class: 'card__title', text: title }),
          hint ? h('span', { class: 'card__hint', text: hint }) : null,
        )
      : h(
          'button',
          {
            type: 'button',
            class: 'fold__btn',
            'aria-expanded': String(open),
            title: open ? `Collapse ${title}` : `Expand ${title}`,
            onclick: toggle,
          },
          icon(ICONS.chevronDown, {
            size: 11,
            width: 2,
            class: open ? 'fold__chev' : 'fold__chev fold__chev--closed',
          }),
          h('span', { class: 'card__title', text: title }),
          hint ? h('span', { class: 'card__hint', text: hint }) : null,
        ),
    trailing.length ? h('span', { class: 'spacer' }) : null,
    ...trailing,
  );
}

/** A horizontal progress meter. */
export function meter(width: string, color: string, height = 4): HTMLSpanElement {
  return h(
    'span',
    { class: 'meter', style: { height: `${height}px`, borderRadius: `${height / 2}px` } },
    h('span', { class: 'meter__fill', style: { width, background: color } }),
  );
}

/**
 * A rating drawn on its own scale: one star per step, filled to the rating.
 *
 * Two layers of the SAME glyph, the top one clipped to `rating / max` of the
 * width. That is what makes a 7.5 show as three quarters of the eighth star
 * rather than as eight, and using one glyph for both layers is what makes the
 * clip land in the right place — an outline glyph from a different codepoint
 * would not have the same metrics, and the two rows would drift apart.
 *
 * `max` is the scale's own top, never a normalised five: a 3 on a 0-4 sheet and a
 * 7 on a 0-10 sheet are different claims, and rescaling them to look alike would
 * be the register asserting something no inspector wrote down.
 */
export function stars(rating: number, max: number): HTMLSpanElement {
  const filled = Math.max(0, Math.min(1, rating / max));
  const row = '★'.repeat(max);

  return h(
    'span',
    {
      class: 'stars',
      role: 'img',
      'aria-label': `${rating} out of ${max}`,
      title: `${rating} out of ${max}`,
    },
    h('span', { class: 'stars__track', 'aria-hidden': 'true', text: row }),
    h('span', {
      class: 'stars__fill',
      'aria-hidden': 'true',
      style: { width: `${filled * 100}%` },
      text: row,
    }),
  );
}

/**
 * A key/value row with a hairline rule.
 *
 * An em dash is how every screen spells "the source document does not record
 * this", so it is set apart here rather than at each of the hundred call sites:
 * a field left blank on a general-arrangement drawing must not read like a
 * measured zero.
 */
export function fact(k: string, v: Child): HTMLDivElement {
  const missing = v === '—' || v == null;
  return h(
    'div',
    /* `data-empty` so a group can count and fold the unrecorded rows without
       reading the em dash back out of the DOM — see `section` in
       src/screens/detail.ts. The decision is made once, here, where it already
       had to be made for the colour. */
    { class: 'fact', ...(missing ? { 'data-empty': '1' } : {}) },
    h('span', { class: 'fact__k', text: k }),
    h('span', { class: ['fact__v', missing && 'fact__v--none'] }, missing ? '—' : v),
  );
}

/**
 * A ranked chip: severity, inspection standing, anything with an order.
 *
 * `rank` runs 1–4 and picks a step of the accent ramp, so the set reads as one
 * scale getting darker rather than as four unrelated colours.
 *
 * `'none'` is the one step OFF that ramp, for a verdict that is not a position
 * on the scale — "not screened", as against low, moderate or high. It is drawn
 * as an outline rather than a fill, because anything filled reads as a value and
 * the whole point of this chip is that there is not one. See `bandRank` in
 * src/data/screening.ts, which is what produces it.
 */
export type TagRank = 1 | 2 | 3 | 4 | 'none';

export function tag(label: string, rank: TagRank = 2, title?: string): HTMLSpanElement {
  return h('span', {
    class: ['tag', `tag--${rank}`],
    ...(title ? { title } : {}),
    text: label,
  });
}

/**
 * A value with a mark saying where it came from.
 *
 * The mark is a superscript beside the figure, not a colour and not an icon: it
 * has to survive the printed sheet, which is monochrome and is the document
 * somebody actually quotes from. A value on the baseline gets no mark at all —
 * marking everything marks nothing, and the legend says what unmarked means.
 *
 * The full sentence is in the `title` as well as in the legend, so the answer is
 * one hover away from the number rather than a scroll away.
 */
export function sourced(value: Child, p: { mark: string; label: string; note: string }): Child {
  if (!p.mark) return value;
  return h(
    'span',
    { class: 'srcv' },
    value,
    h('sup', { class: 'srcv__mark', title: `${p.label} — ${p.note}`, text: p.mark }),
  );
}

/**
 * What the marks on a block mean.
 *
 * Always carries the unmarked case first, even when every value on the block is
 * marked: "everything here is weaker than the drawing" is exactly the sentence a
 * reader needs, and a legend that only lists exceptions cannot say it.
 */
export function sourceLegend(entries: { mark: string; label: string; note: string }[]): HTMLElement {
  return h(
    'div',
    { class: 'srcl' },
    h('div', {
      class: 'srcl__row',
      text: 'Unmarked values are transcribed from the as-built drawing.',
    }),
    ...entries
      .filter((e) => e.mark)
      .map((e) =>
        h(
          'div',
          { class: 'srcl__row' },
          h('span', { class: 'srcv__mark srcl__key', text: e.mark }),
          h('span', {}, h('strong', { text: e.label }), ` — ${e.note}`),
        ),
      ),
  );
}

/** One figure in the overview's headline strip. */
export function statTile(k: { label: string; value: string; unit: string; sub?: string }): HTMLDivElement {
  return card(
    { pad: true },
    h('div', { class: 'kpi__label', text: k.label }),
    h(
      'div',
      { class: 'kpi__row' },
      h('span', { class: 'kpi__value', text: k.value }),
      h('span', { class: 'kpi__unit', text: k.unit }),
    ),
    k.sub ? h('div', { class: 'kpi__sub', text: k.sub }) : null,
  );
}

/** A named bar with its figure — the overview's freeboard and share charts. */
export function barRow(name: string, width: string, value: string, onClick?: () => void): HTMLElement {
  return h(
    'div',
    { class: 'barrow', ...(onClick ? { onclick: onClick, style: 'cursor:pointer' } : {}) },
    h('div', { class: 'barrow__name', title: name, text: name }),
    meter(width, 'var(--accent-solid)', 14),
    h('div', { class: 'barrow__value', text: value }),
  );
}

/** A segmented control. */
export interface SegOption {
  label: string;
  on: boolean;
  onClick: () => void;
  dot?: string;
  title?: string;
  /** Icon INSTEAD of the label — a square, icon-only option. */
  iconPath?: IconPath;
  /** Icon BESIDE the label, before it. */
  leadIcon?: IconPath;
}

export function seg(options: SegOption[]): HTMLDivElement {
  return h(
    'div',
    { class: 'seg', role: 'group' },
    ...options.map((o) =>
      h(
        'button',
        {
          type: 'button',
          class: ['seg__opt', o.on && 'seg__opt--on', o.iconPath && 'seg__opt--icon'],
          'aria-pressed': String(o.on),
          ...(o.title ? { title: o.title } : {}),
          onclick: o.onClick,
        },
        o.dot ? h('span', { class: 'seg__dot', style: { background: o.dot } }) : null,
        o.leadIcon && !o.iconPath ? icon(o.leadIcon, { size: 14, width: 1.8 }) : null,
        o.iconPath ? icon(o.iconPath, { size: 13, width: 1.4 }) : o.label,
      ),
    ),
  );
}

/** A selectable chip. */
export function chip(label: string, on: boolean, onClick: () => void): HTMLButtonElement {
  return h(
    'button',
    {
      type: 'button',
      class: ['chip', on && 'chip--on'],
      'aria-pressed': String(on),
      onclick: onClick,
    },
    label,
  );
}

/**
 * A labelled form field wrapping an arbitrary control.
 *
 * `forId` is the control's own id, when it has one — which every text control in
 * a dialog needs anyway, since src/main.ts restores focus and caret across a
 * re-render by id. Passing it here means the label points at the control it
 * names rather than sitting beside it unassociated.
 */
export function field(label: string | Node, control: Child, forId?: string, hint?: string): HTMLDivElement {
  return h(
    'div',
    { class: 'field', ...(hint ? { title: hint } : {}) },
    h(
      'label',
      { ...(forId ? { for: forId } : {}), class: [hint && 'field__label--hint'] },
      label,
      hint ? icon(ICONS.help, { size: 11, stroke: 'currentColor' }) : null,
    ),
    control,
  );
}

/** A button with an optional leading icon. */
export function button(
  label: string,
  opts: {
    primary?: boolean;
    /** An action that destroys something. Reads in the critical colour. */
    danger?: boolean;
    block?: boolean;
    grow?: boolean;
    tall?: boolean;
    sm?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit';
    iconPath?: IconPath;
    title?: string;
    onClick?: () => void;
  } = {},
): HTMLButtonElement {
  return h(
    'button',
    {
      type: opts.type ?? 'button',
      disabled: opts.disabled || undefined,
      class: [
        'btn',
        opts.primary && 'btn--primary',
        opts.danger && 'btn--danger',
        opts.block && 'btn--block',
        opts.grow && 'btn--grow',
        opts.tall && 'btn--tall',
        opts.sm && 'btn--sm',
      ],
      ...(opts.title ? { title: opts.title } : {}),
      ...(opts.onClick ? { onclick: opts.onClick } : {}),
    },
    opts.iconPath ? icon(opts.iconPath, { size: 12, width: 1.8 }) : null,
    label,
  );
}

/** An empty-state row for tables and lists. */
export function emptyState(message: string): HTMLDivElement {
  return h(
    'div',
    { class: 'reg__empty' },
    icon(ICONS.search, { size: 20, stroke: 'var(--txt3)' }),
    h('div', { style: 'margin-top:6px', text: message }),
  );
}

/**
 * A review picker's change handler that puts the picker back where it was when
 * the register refuses the move (QA 2026-09-30). The store keeps what is stored,
 * so nothing repaints the `<select>` a person just moved; this does.
 */
export function revertOnRefusal(stored: string, act: (value: string) => Promise<boolean>): (e: Event) => void {
  return (e: Event) => {
    const select = e.target as HTMLSelectElement;
    void act(select.value).then((ok) => {
      if (!ok) select.value = stored;
    });
  };
}

/** Why the register refused a move on this row, under its picker, or nothing. */
export function refusalNote(refused: { id: string; message: string } | null, id: string): HTMLElement | null {
  return refused !== null && refused.id === id
    ? h('div', { class: 'refusal', role: 'alert', text: refused.message })
    : null;
}
